import { CartAddEvent } from '@theme/events';

/**
 * Cart Order Bump Product Component
 * Manages individual product interactions in the order bump carousel
 */
class CartOrderBumpProduct extends HTMLElement {
  constructor() {
    super();
    this.productData = null;
    this.variantsData = null;
    this.currentVariantId = null;
  }

  connectedCallback() {
    this.#loadProductData();
    this.#setupEventListeners();
    this.currentVariantId = this.getAttribute('data-default-variant-id');
  }

  /**
   * Loads product and variants data from script tags
   */
  #loadProductData() {
    try {
      const productScript = this.querySelector('script[data-product]');
      const variantsScript = this.querySelector('script[data-variants]');
      
      if (productScript && variantsScript && productScript.textContent && variantsScript.textContent) {
        this.productData = JSON.parse(productScript.textContent);
        this.variantsData = JSON.parse(variantsScript.textContent);
        return;
      }
    } catch (error) {
      console.error('Error loading product data:', error);
    }

    // Slides da esteira vêm sem JSON inline: os 7 candidatos somam 118 variantes e
    // o drawer re-renderiza a cada mudança de carrinho. Busca sob demanda mantém o
    // HTML no tamanho de hoje. O card já veio pronto do Liquid na variante padrão —
    // o JSON só é necessário pra TROCAR de variante.
    const lazyHandle = this.getAttribute('data-lazy-product');
    if (lazyHandle) this.#fetchProductData(lazyHandle);
  }

  /**
   * Carrega produto/variantes de um slide lazy da esteira.
   * @param {string} handle
   */
  async #fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return;
      const data = await response.json();
      this.productData = data;
      this.variantsData = data.variants || [];
    } catch (error) {
      // Falha silenciosa: sem o JSON o seletor de variante não troca, mas o card
      // segue vendável na variante padrão que o Liquid já renderizou no botão.
    }
  }

  /**
   * Sets up event listeners for variant selection and add to cart
   */
  #setupEventListeners() {
    // Listen for variant changes
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.classList.contains('cart-order-bump__variant-select')) {
        this.#handleVariantChange();
      }
    });

    // Listen for add to cart button clicks
    this.addEventListener('click', (event) => {
      if (event.target instanceof HTMLElement && event.target.closest('[data-add-button]')) {
        event.preventDefault();
        this.#handleAddToCart();
      }
    });
  }

  /**
   * Handles variant selection changes
   */
  #handleVariantChange() {
    const selects = this.querySelectorAll('.cart-order-bump__variant-select');
    const selectedOptions = Array.from(selects).map(select => {
      return select instanceof HTMLSelectElement ? select.value : '';
    });
    
    const selectedVariant = this.#findVariantByOptions(selectedOptions);
    
    if (selectedVariant) {
      this.currentVariantId = String(selectedVariant.id);
      this.#updateProductDisplay(selectedVariant);
    }
  }

  /**
   * Finds variant by selected options
   * @param {string[]} selectedOptions 
   * @returns {any|null}
   */
  #findVariantByOptions(selectedOptions) {
    if (!this.variantsData) return null;
    return this.variantsData.find((/** @type {any} */ variant) => {
      return variant.options && variant.options.every((/** @type {any} */ option, /** @type {number} */ index) => option === selectedOptions[index]);
    }) || null;
  }

  /**
   * Updates product display based on selected variant
   * @param {any} variant 
   */
  #updateProductDisplay(variant) {
    // Update image
    const img = this.querySelector('[data-product-image]');
    if (img instanceof HTMLImageElement && variant?.featured_image) {
      img.src = variant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_200x200.$1');
      img.alt = variant.featured_image.alt || this.productData?.title || '';
    }

    // Update prices
    this.#updatePrices(variant);

    // Update availability
    this.#updateAvailability(variant);

    // Update button variant ID
    const button = this.querySelector('[data-add-button]');
    if (button && variant?.id) {
      button.setAttribute('data-variant-id', String(variant.id));
    }
  }

  /**
   * Updates price display
   * @param {any} variant 
   */
  #updatePrices(variant) {
    const priceContainer = this.querySelector('[data-price-container]');
    const comparePrice = this.querySelector('[data-compare-price]');
    const discountLabel = this.querySelector('[data-discount-label]');
    const finalPrice = this.querySelector('[data-final-price]');

    if (!finalPrice) return;

    /* T1 (09/2026): daqui pra frente o JS recalcula sozinho, no percentual do braco
       certo. Os `data-t1-*` do render inicial (Liquid) ficariam desatualizados e o
       switch global os reaplicaria por cima do valor recem-calculado — por isso saem. */
    const t1Ativo = document.documentElement.classList.contains('t1-20');
    if (t1Ativo) {
      finalPrice.removeAttribute('data-t1-price');
      if (discountLabel instanceof HTMLElement) discountLabel.removeAttribute('data-t1-badge');
    }

    const variantPrice = variant?.price || 0;

    // Prioridade 1: desconto_percentual da oferta (mesma lógica do Liquid PR #30)
    // Cálculo em escala x10 pra preservar precisão de decimais (ex: 21.6%).
    const offerPctRaw = priceContainer instanceof HTMLElement
      ? (t1Ativo
          ? (priceContainer.getAttribute('data-t1-offer-discount-percent') || priceContainer.getAttribute('data-offer-discount-percent'))
          : priceContainer.getAttribute('data-offer-discount-percent'))
      : '';
    const offerPct = offerPctRaw ? parseFloat(offerPctRaw) : 0;

    if (offerPct > 0) {
      const offerDiscountX10 = Math.round(offerPct * 10);
      const discountAmount = Math.floor((variantPrice * offerDiscountX10) / 1000);
      const finalCents = variantPrice - discountAmount;

      this.#renderFinalPrice(finalPrice, finalCents, false);

      if (comparePrice instanceof HTMLElement) {
        comparePrice.textContent = this.#formatMoney(variantPrice);
        comparePrice.style.display = '';
      }

      if (discountLabel instanceof HTMLElement) {
        discountLabel.textContent = `${Math.round(offerPct)}% OFF`;
        discountLabel.style.display = '';
      }
      return;
    }

    // Fallback: compare_at_price do produto
    this.#renderFinalPrice(finalPrice, variantPrice, false);

    if (variant?.compare_at_price && variant.compare_at_price > variant.price) {
      if (comparePrice instanceof HTMLElement) {
        comparePrice.textContent = this.#formatMoney(variant.compare_at_price);
        comparePrice.style.display = '';
      }

      if (discountLabel instanceof HTMLElement) {
        const discountPercent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        discountLabel.textContent = `${discountPercent}% OFF`;
        discountLabel.style.display = '';
      }
    } else {
      if (comparePrice instanceof HTMLElement) comparePrice.style.display = 'none';
      if (discountLabel instanceof HTMLElement) discountLabel.style.display = 'none';
    }
  }

  /**
   * Escreve o preço final preservando o sufixo "no Pix" quando ele faz parte do preço.
   * `textContent` sozinho apagava o `<span class="cart-order-bump__price-pix">` que o
   * Liquid renderiza, e o card perdia a informação de que aquele valor é o total no Pix.
   * @param {Element} finalPrice
   * @param {number} cents
   * @param {boolean} isPixPrice
   */
  /* 14/08/2026 — regime flat 15/32: o preço do card vale pra qualquer forma de pagamento,
     então o sufixo "no Pix" saiu. O parâmetro `isPixPrice` continua na assinatura de
     propósito: os call sites passam o flag que o Liquid emite, e mudar a assinatura junto
     com a semântica esconderia o que de fato mudou. Ele agora não governa nada. */
  #renderFinalPrice(finalPrice, cents, _isPixPrice) {
    finalPrice.textContent = this.#formatMoney(cents);
  }

  /**
   * Updates availability state
   * @param {any} variant
   */
  #updateAvailability(variant) {
    const button = this.querySelector('[data-add-button]');
    const buttonText = this.querySelector('[data-add-text]');

    if (!(button instanceof HTMLButtonElement) || !buttonText) return;

    if (variant?.available) {
      button.disabled = false;
      // 11/08: o rótulo vem do Liquid (texto do metaobjeto/tema). Estava chumbado em
      // "Quero aproveitar", então trocar de variante trocava o CTA configurado.
      buttonText.textContent = button.getAttribute('data-add-text-available') || 'Quero aproveitar';
    } else {
      button.disabled = true;
      buttonText.textContent = 'Indisponível';
    }
  }

  /**
   * Handles add to cart button click
   */
  async #handleAddToCart() {
    const button = this.querySelector('[data-add-button]');
    const spinner = this.querySelector('[data-spinner]');
    const buttonText = this.querySelector('[data-add-text]');

    if (!this.currentVariantId || !(button instanceof HTMLButtonElement) || button.disabled) return;

    // Show loading state
    this.#setLoadingState(true);

    try {
      const formData = {
        id: this.currentVariantId,
        quantity: 1
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Show success state briefly
        this.#setSuccessState();

        // Distinção de trackeamento (11/08): variant pode ser null (slide lazy cujo
        // fetch falhou) — o trackingData não depende dele, só enriquece com ele.
        const variant = this.variantsData
          && this.variantsData.find((/** @type {any} */ v) => v.id == this.currentVariantId);
        const trackingData = this.#buildTrackingData(variant);

        // Dispatch custom order bump event for analytics
        this.#dispatchOrderBumpEvent(result, variant, trackingData);

        // Censo no pedido: fire-and-forget, nunca segura o fluxo de compra
        this.#recordAcceptOnCart(trackingData);

        // Dispatch CartAddEvent to update cart drawer (this handles everything automatically)
        this.#dispatchCartUpdateEvent(result);

      } else {
        throw new Error('Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.#showError();
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * Sets loading state
   * @param {boolean} loading 
   */
  #setLoadingState(loading) {
    const button = this.querySelector('[data-add-button]');
    const spinner = this.querySelector('[data-spinner]');

    if (!(button instanceof HTMLButtonElement)) return;

    if (loading) {
      // Remove outros estados primeiro
      button.classList.remove('cart-order-bump__add-button--success', 'cart-order-bump__add-button--error');
      // Adiciona estado de loading
      button.classList.add('cart-order-bump__add-button--loading');
      button.disabled = true;
      
      // Força o spinner a aparecer como fallback
      if (spinner instanceof HTMLElement) {
        spinner.style.display = 'block';
        spinner.style.position = 'absolute';
        spinner.style.top = '50%';
        spinner.style.left = '50%';
        spinner.style.transform = 'translate(-50%, -50%)';
      }
    } else {
      // Remove estado de loading
      button.classList.remove('cart-order-bump__add-button--loading');
      button.disabled = false;
      
      // Esconde spinner
      if (spinner instanceof HTMLElement) {
        spinner.style.display = 'none';
      }
    }
  }

  /**
   * Sets success state briefly
   */
  #setSuccessState() {
    const button = this.querySelector('[data-add-button]');
    const buttonText = this.querySelector('[data-add-text]');

    if (!(button instanceof HTMLButtonElement) || !buttonText) return;

    // Show success state
    button.classList.add('cart-order-bump__add-button--success');
    button.disabled = true; // Disable button during success state
    const originalText = buttonText.textContent;
    buttonText.textContent = 'Adicionado!';

    // Reset after 1.5 seconds
    setTimeout(() => {
      button.classList.remove('cart-order-bump__add-button--success');
      button.disabled = false; // Re-enable button
      buttonText.textContent = originalText;
    }, 1500);
  }

  /**
   * Distinção de trackeamento (11/08/2026): de ONDE saiu a oferta aceita.
   * `ob_logic` (container `data-ob-logic`) separa a lógica vigente de uma futura
   * variante em teste; `ob_layer` separa gatilho de esteira. O mesmo objeto
   * alimenta o dataLayer E o censo no pedido — uma fonte, duas saídas.
   * @param {any} variant
   */
  #buildTrackingData(variant) {
    const slide = this.closest('.cart-order-bump__item');
    const container = document.querySelector('#cart-order-bump');
    return {
      ob_logic: (container && container.getAttribute('data-ob-logic')) || 'estatico',
      ob_layer: this.hasAttribute('data-lazy-product') ? 'esteira' : 'gatilho',
      ob_handle: (slide && slide.getAttribute('data-cs-handle')) || '',
      ob_identity: (slide && slide.getAttribute('data-cs-identity')) || '',
      ob_variant_id: String(this.currentVariantId || ''),
      ob_variant_title: (variant && variant.title) || ''
    };
  }

  /**
   * Censo no PEDIDO: grava o aceite como atributo de CARRINHO (`_ob_accepts`),
   * que a Shopify copia pros note_attributes do pedido — leitura imune a
   * adblocker, direto no dado da loja.
   *
   * Por que atributo de carrinho e NÃO property de linha: (a) property nova faz
   * o /cart/add.js criar linha separada em vez de somar quantidade quando a
   * esteira oferta um produto que já está no carrinho; (b) a Function
   * `hoomy-ob-validation` classifica linha por attr/tag/título e tem regra que
   * BLOQUEIA checkout — marca de linha nova aqui é adjacência com dinheiro.
   * Atributo de carrinho é neutro nos dois.
   *
   * Acumulador com semente única por sessão (sessionStorage): evita um GET por
   * aceite e o clobber entre dois aceites rápidos. Edge conhecido e aceito:
   * carrinho retomado em OUTRA sessão re-semeia do /cart.js — se este POST
   * ainda não tinha acontecido, o registro anterior da mesma sessão prevalece.
   * @param {{ob_logic: string, ob_layer: string, ob_handle: string, ob_variant_id: string}} trackingData
   */
  async #recordAcceptOnCart(trackingData) {
    try {
      const KEY = 'hoomy_ob_accepts';
      const entry = [
        trackingData.ob_logic,
        trackingData.ob_layer,
        trackingData.ob_handle,
        trackingData.ob_variant_id,
        Math.floor(Date.now() / 1000)
      ].join('|');

      let base = '';
      try {
        base = sessionStorage.getItem(KEY) || '';
      } catch (error) {
        // sem sessionStorage a semente vem do /cart.js a cada aceite
      }
      if (!base) {
        const res = await fetch('/cart.js');
        if (res.ok) {
          const cart = await res.json();
          base = (cart.attributes && cart.attributes._ob_accepts) || '';
        }
      }

      const value = base ? `${base};${entry}` : entry;
      try {
        sessionStorage.setItem(KEY, value);
      } catch (error) {
        // idem acima
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _ob_accepts: value } })
      });
    } catch (error) {
      // Censo é observabilidade: nunca pode derrubar o fluxo de compra.
    }
  }

  /**
   * Dispatches custom order bump event for analytics
   * @param {any} cartData
   * @param {any} variant
   * @param {any} trackingData
   */
  #dispatchOrderBumpEvent(cartData, variant, trackingData) {
    // Custom event for analytics/datalayer
    const orderBumpEvent = new CustomEvent('orderBumpAdd', {
      bubbles: true,
      detail: {
        product: this.productData,
        variant: variant,
        cartData: cartData,
        source: 'cart-order-bump'
      }
    });

    document.dispatchEvent(orderBumpEvent);

    // DataLayer event for Google Analytics.
    // (11/08) Sem o gate em `variant`: slide lazy cujo /products/<handle>.js
    // falhou não tem variantsData e o aceite sumia do dataLayer — a distinção
    // (ob_data) não depende do JSON do produto, só os campos de ecommerce.
    if (typeof window !== 'undefined' && /** @type {any} */ (window).dataLayer) {
      // Zera antes de reusar a chave (convenção `ecommerce: null` do tema):
      // valor de push persiste no modelo do data layer e vazaria pra outras tags.
      /** @type {any} */ (window).dataLayer.push({ ob_data: null });
      /** @type {any} */ (window).dataLayer.push({
        event: 'order_bump_add_to_cart',
        ob_data: trackingData,
        ecommerce: {
          currency: 'BRL',
          value: ((variant && variant.price) || 0) / 100,
          items: [{
            item_id: (variant && (variant.sku || variant.id)) || trackingData.ob_variant_id,
            item_name: this.productData?.title || trackingData.ob_handle,
            item_variant: (variant && variant.title) || '',
            price: ((variant && variant.price) || 0) / 100,
            quantity: 1
          }]
        }
      });
    }
  }

  /**
   * Dispatches standard cart update event
   * @param {any} cartData 
   */
  #dispatchCartUpdateEvent(cartData) {
    // Use the same CartAddEvent that pdp-bundle uses to properly update cart drawer
    document.dispatchEvent(new CartAddEvent(
      cartData,
      this.currentVariantId || '',
      {
        source: 'cart-order-bump',
        itemCount: cartData && cartData.item_count ? cartData.item_count : 1,
        productId: this.productData && this.productData.id ? this.productData.id : ''
      }
    ));
  }



  /**
   * Shows error state
   */
  #showError() {
    const button = this.querySelector('[data-add-button]');
    const buttonText = this.querySelector('[data-add-text]');
    
    if (!(button instanceof HTMLButtonElement) || !buttonText) return;

    // Show error state
    button.classList.add('cart-order-bump__add-button--error');
    button.disabled = true; // Disable button during error state
    const originalText = buttonText.textContent;
    buttonText.textContent = 'Erro. Tente novamente';
    
    // Reset after 3 seconds
    setTimeout(() => {
      button.classList.remove('cart-order-bump__add-button--error');
      button.disabled = false; // Re-enable button
      buttonText.textContent = originalText;
    }, 3000);
  }

  /**
   * Formats money using Shopify's money format
   * @param {number} cents 
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = cents / 100;
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }
}

customElements.define('cart-order-bump-product', CartOrderBumpProduct); 